import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getWorkItemsForCalendar } from '../../api/workItems';
import UserSelect from '../common/UserSelect';
import './WorkItemCalendar.scss';

// ── 日期工具 ──
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dayDiff = (a, b) => Math.round((a.getTime() - b.getTime()) / 86400000);
const WEEK_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const MAX_LANES = 3; // 每週最多顯示的橫桿層,超過收成「+N」

// 專案色帶(橫桿底色);無專案給中性色
const barColor = (it) => it.projects?.color || '#98a2b3';

const WorkItemCalendar = ({ companyId, companyUsers = [], onOpenItem }) => {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dayPopover, setDayPopover] = useState(null);
  const [levelFilter, setLevelFilter] = useState('all'); // all | parent | child
  const [assigneeFilter, setAssigneeFilter] = useState(null); // user_id | null
  const todayStr = ymd(now);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await getWorkItemsForCalendar({ companyId });
    // 補齊 effective 起訖:只有單邊日期的當單日
    setItems((data || []).map((it) => ({
      ...it,
      _s: it.start_date || it.end_date,
      _e: it.end_date || it.start_date,
    })));
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // 父/子層 + Owner/指派人 篩選
  const filteredItems = useMemo(() => {
    let arr = items;
    if (levelFilter === 'parent') arr = arr.filter((it) => !it.parent_id);
    else if (levelFilter === 'child') arr = arr.filter((it) => it.parent_id);
    // 指派人篩選:該 work_item 的 assignees 含選定者(涵蓋 Owner,因 Owner 也是 assignee)
    if (assigneeFilter) arr = arr.filter((it) => (it.assignees || []).some((a) => a.user_id === assigneeFilter));
    return arr;
  }, [items, levelFilter, assigneeFilter]);

  // 6 週 × 7 天(前後月補齊)
  const weeks = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const gridStart = new Date(cursor.y, cursor.m, 1 - first.getDay());
    const ws = [];
    for (let w = 0; w < 6; w++) {
      const days = [];
      for (let d = 0; d < 7; d++) {
        days.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + w * 7 + d));
      }
      ws.push(days);
    }
    return ws;
  }, [cursor]);

  // 每週:與該週交集的 work_item → 算 startCol/endCol → 貪婪分配 lane
  const weekBars = useCallback((week) => {
    const weekStart = ymd(week[0]);
    const weekEnd = ymd(week[6]);
    const hit = filteredItems
      .filter((it) => it._s && it._e && it._s <= weekEnd && it._e >= weekStart)
      .map((it) => ({
        it,
        startCol: Math.max(0, dayDiff(new Date(it._s.replace(/-/g, '/')), week[0])),
        endCol: Math.min(6, dayDiff(new Date(it._e.replace(/-/g, '/')), week[0])),
      }))
      .sort((a, b) => a.startCol - b.startCol || b.endCol - a.endCol);

    const lanes = []; // 每個 lane 記最後一根的 endCol
    const placed = [];
    const overflow = {}; // col → 被擠掉的數量
    hit.forEach((bar) => {
      let lane = lanes.findIndex((lastEnd) => lastEnd < bar.startCol);
      if (lane === -1) { lanes.push(bar.endCol); lane = lanes.length - 1; }
      else lanes[lane] = bar.endCol;
      if (lane < MAX_LANES) placed.push({ ...bar, lane });
      else for (let c = bar.startCol; c <= bar.endCol; c++) overflow[c] = (overflow[c] || 0) + 1;
    });
    return { placed, overflow };
  }, [filteredItems]);

  const goMonth = (delta) => setCursor((c) => {
    const d = new Date(c.y, c.m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const goToday = () => setCursor({ y: now.getFullYear(), m: now.getMonth() });

  // 點日期格 → 列出當天所有工作項目(含被 +N 收起的)
  const openDay = (ds, el) => {
    const list = filteredItems.filter((it) => it._s && it._e && it._s <= ds && it._e >= ds);
    if (!list.length) { setDayPopover(null); return; }
    const r = el.getBoundingClientRect();
    setDayPopover({ date: ds, list, x: Math.min(r.left, window.innerWidth - 310), y: r.bottom });
  };

  // Owner = assignees 最早 assigned 的那位 → 對 companyUsers 解析出名字
  const ownerOf = (it) => {
    const a = (it.assignees || []).slice().sort((x, y) => (x.assigned_at || '').localeCompare(y.assigned_at || ''))[0];
    return a ? (companyUsers.find((u) => u.id === a.user_id) || null) : null;
  };

  return (
    <div className="wi-cal">
      <div className="wi-cal-head">
        <div className="wi-cal-nav">
          <button onClick={() => goMonth(-1)} aria-label="上個月">‹</button>
          <span className="wi-cal-title">{cursor.y} 年 {cursor.m + 1} 月</span>
          <button onClick={() => goMonth(1)} aria-label="下個月">›</button>
          <button className="wi-cal-today" onClick={goToday}>今天</button>
          {loading && <span className="wi-cal-hint">載入中…</span>}
        </div>
        <div className="wi-cal-filter">
          {[['all', '全部'], ['parent', '工作項目'], ['child', '子項']].map(([v, label]) => (
            <button
              key={v}
              className={`wi-cal-fbtn ${levelFilter === v ? 'active' : ''}`}
              onClick={() => setLevelFilter(v)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="wi-cal-people">
          <UserSelect
            values={assigneeFilter ? [assigneeFilter] : []}
            onChange={(ids) => setAssigneeFilter(ids[0] || null)}
            users={companyUsers}
            placeholder="Owner / 指派人"
            singleSelect
            portal
          />
          {assigneeFilter && (
            <button className="wi-cal-people-clear" onClick={() => setAssigneeFilter(null)} title="清除人員篩選">×</button>
          )}
        </div>
      </div>

      <div className="wi-cal-weekhead">
        {WEEK_LABELS.map((w) => <div key={w} className="wi-cal-wh">{w}</div>)}
      </div>

      <div className="wi-cal-grid">
        {weeks.map((week, wi) => {
          const { placed, overflow } = weekBars(week);
          const laneCount = Math.min(MAX_LANES, placed.reduce((mx, b) => Math.max(mx, b.lane + 1), 0));
          return (
            <div key={wi} className="wi-cal-week">
              {/* 日期格背景 */}
              <div className="wi-cal-daycells">
                {week.map((d) => {
                  const inMonth = d.getMonth() === cursor.m;
                  const ds = ymd(d);
                  const of = overflow[week.indexOf(d)];
                  return (
                    <div
                      key={ds}
                      className={`wi-cal-cell ${inMonth ? '' : 'muted'} ${ds === todayStr ? 'today' : ''}`}
                      onClick={(e) => openDay(ds, e.currentTarget)}
                    >
                      <span className="wi-cal-daynum">{d.getDate()}</span>
                      {of > 0 && <span className="wi-cal-more">+{of}</span>}
                    </div>
                  );
                })}
              </div>
              {/* 橫桿層 */}
              <div className="wi-cal-bars" style={{ height: `${laneCount * 22 + 4}px` }}>
                {placed.map(({ it, startCol, endCol, lane }) => (
                  <button
                    key={it.id}
                    className={`wi-cal-bar ${it.parent_id ? 'is-sub' : ''}`}
                    style={{
                      left: `calc(${(startCol / 7) * 100}% + 2px)`,
                      width: `calc(${((endCol - startCol + 1) / 7) * 100}% - 4px)`,
                      top: `${lane * 22}px`,
                      background: barColor(it),
                    }}
                    title={`${it.work_item_no ? `#${it.work_item_no} ` : ''}${it.title}`}
                    onClick={() => onOpenItem?.(it)}
                  >
                    {(() => { const o = ownerOf(it); return o ? <span className="wi-cal-bar-ava" title={`Owner：${o.name}`}>{(o.name || '?').charAt(0)}</span> : null; })()}
                    <span className="wi-cal-bar-txt">{it.work_item_no ? `#${it.work_item_no} ` : ''}{it.title}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {dayPopover && createPortal(
        <div className="wi-cal-pop-wrap">
          <div className="wi-cal-pop-mask" onMouseDown={() => setDayPopover(null)} />
          <div className="wi-cal-pop" style={{ left: `${dayPopover.x}px`, top: `${dayPopover.y + 4}px` }}>
            <div className="wi-cal-pop-head">{dayPopover.date}（{dayPopover.list.length}）</div>
            <div className="wi-cal-pop-list">
              {dayPopover.list.map((it) => (
                <button
                  key={it.id}
                  className="wi-cal-pop-item"
                  onClick={() => { onOpenItem?.(it); setDayPopover(null); }}
                >
                  <span className="wi-cal-pop-dot" style={{ background: barColor(it) }} />
                  <span className="wi-cal-pop-txt">{it.work_item_no ? `#${it.work_item_no} ` : ''}{it.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default WorkItemCalendar;
